import { motion } from "framer-motion";
import { CalendarDays, CheckCircle2, FileText, Inbox, Send, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
    {
        id: "enquire",
        title: "Enquire",
        body: "Capture customer enquiries, notes and opportunities in one place — with AI helping prioritise them so nothing is missed from the start.",
        icon: Inbox,
        color: "#00E5FF",
    },
    {
        id: "schedule",
        title: "Schedule",
        body: "Plan jobs and organise workloads with AI-assisted scheduling that matches the right engineer to every job.",
        icon: CalendarDays,
        color: "#0066FF",
    },
    {
        id: "dispatch",
        title: "Dispatch",
        body: "Send the right job to the right engineer with clear details, directions and customer information — optimised automatically.",
        icon: Send,
        color: "#7C5CFC",
    },
    {
        id: "complete",
        title: "Complete",
        body: "Record work with digital forms, photos, signatures and parts used — with AI helping capture complete, accurate data from the field.",
        icon: CheckCircle2,
        color: "#A855F7",
    },
    {
        id: "invoice",
        title: "Invoice",
        body: "Turn completed work into accurate invoicing faster, with AI checking job records before they reach accounts.",
        icon: FileText,
        color: "#D946EF",
    },
];

const Step = ({ step, index }) => {
    const Icon = step.icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.15 + index * 0.1 }}
            className="group relative z-10 flex flex-col items-center text-center"
            data-testid={`workflow-step-${index + 1}`}
        >
            <div
                className="relative flex h-16 w-16 items-center justify-center rounded-2xl border bg-[#0B1220]/80 backdrop-blur-sm transition-all duration-300 group-hover:-translate-y-1.5"
                style={{
                    borderColor: `${step.color}55`,
                    boxShadow: `0 0 0 0 ${step.color}00`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 8px 30px -6px ${step.color}66`)}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = `0 0 0 0 ${step.color}00`)}
            >
                <Icon className="h-6 w-6" style={{ color: step.color }} strokeWidth={1.75} />
                <span
                    className="absolute -right-1.5 -top-1.5 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-[#0066FF] to-[#A855F7] px-1.5 py-0.5 text-[8px] font-bold tracking-wider text-white shadow-md"
                    title="AI-assisted step"
                >
                    <Sparkles className="h-2 w-2" />
                    AI
                </span>
            </div>
            <h3 className="mt-6 font-display text-lg font-bold text-[#F8FAFF]">{step.title}</h3>
            <p className="mt-3 max-w-[240px] text-sm leading-relaxed text-[#94A3B8]">{step.body}</p>
        </motion.div>
    );
};

export default function Workflow() {
    return (
        <section
            id="how-it-works"
            className="relative overflow-hidden bg-[#0A0F1C]"
            data-testid="workflow-section"
            aria-labelledby="workflow-heading"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(circle at 8% 0%, rgba(0,229,255,0.07) 0%, transparent 45%), radial-gradient(circle at 95% 100%, rgba(217,70,239,0.08) 0%, transparent 50%)",
                }}
            />
            <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/5 px-4 py-1.5"
                            data-testid="workflow-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#7DD3FC] sm:text-xs">
                                The TedOps Workflow
                            </span>
                        </span>
                        <h2
                            id="workflow-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#F8FAFF] sm:text-5xl"
                            data-testid="workflow-heading"
                        >
                            Smarter Workflows.
                            <br />
                            <span className="text-brand-gradient">Powered by AI.</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                        className="max-w-md"
                    >
                        <span aria-hidden="true" className="mb-6 block h-px w-10 bg-[#334155]" />
                        <p className="text-base leading-relaxed text-[#94A3B8]" data-testid="workflow-intro">
                            TedOps connects your enquiries, scheduling, field teams, stock and
                            invoicing into one intelligent workflow — with AI reducing admin,
                            preventing missed jobs and helping service businesses move faster.
                        </p>
                    </motion.div>
                </div>

                <div className="relative mt-16 lg:mt-24">
                    <div
                        aria-hidden="true"
                        className="absolute left-[10%] right-[10%] top-8 hidden h-px lg:block"
                        style={{
                            background:
                                "linear-gradient(to right, #00E5FF66, #0066FF66, #7C5CFC66, #A855F766, #D946EF66)",
                        }}
                    />
                    <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-5 lg:gap-6">
                        {STEPS.map((step, i) => (
                            <Step key={step.id} step={step} index={i} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
